import { FC, useContext, useMemo, useState } from "react";
import { useParams } from "react-router";

import { Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { DeepMutable } from "capitalrx-types";

import { createPaymentPreferenceAPI, getProviderManagementDocumentsUrlAPI, updatePaymentPreferenceAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, processDocumentFiles, uploadFileToS3 } from "@/utils";

import { getDocumentsByType } from "../../components";
import {
  PaymentRemittanceGroupFields,
  ProviderPaymentRemittanceDataForm,
  checkSameRemittanceAddress,
} from "../../components/PaymentRemittance/PaymentRemittanceGroupFields";

interface PaymentRemittanceEditProps {
  readonly provider: ProviderManagement.ProviderSchema;
  readonly documents: ProviderManagement.DocumentSchema[];
  readonly setProvider: Function;
  readonly paymentPreference: ProviderManagement.PaymentPreferenceSchema;
  readonly setPaymentPreference: Function;
  setIsEditPayment: Function;
}

export const PaymentRemittanceEdit: FC<PaymentRemittanceEditProps> = ({
  provider,
  documents,
  paymentPreference,
  setPaymentPreference,
  setIsEditPayment,
}) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();
  const { primary_color } = useContext(ThemeSelectorContext);
  const [documentList, setDocumentList] = useState<ProviderManagement.DocumentSchema[]>(documents);
  const { providerNetworkId } = useParams();

  const documentsByType = useMemo(() => {
    return getDocumentsByType(documents);
  }, [documents]);

  const defaultValues: ProviderPaymentRemittanceDataForm = {
    opt_out: false,
    payment_type: paymentPreference?.payment_type || ProviderManagement.PAYMENT_TYPE_VALUES.CHECK,
    financial_institutions: {
      name: paymentPreference?.financial_institution_name,
      street_address: paymentPreference?.financial_institution_address_line_1,
      address_line: paymentPreference?.financial_institution_address_line_2,
      city: paymentPreference?.financial_institution_address_city,
      state: paymentPreference?.financial_institution_address_state,
      zip_code: paymentPreference?.financial_institution_address_zip_code,
      first_name: paymentPreference?.financial_institution_contact_first_name,
      last_name: paymentPreference?.financial_institution_contact_last_name,
      phone_number: paymentPreference?.financial_institution_phone,
      fax_number: paymentPreference?.financial_institution_fax,
    },
    type_account: paymentPreference?.ach_account_type,
    routing_number: paymentPreference?.ach_routing_number,
    bank_account_number: paymentPreference?.ach_bank_account_number,
    account_owner_first_name: paymentPreference?.account_owner_first_name,
    account_owner_last_name: paymentPreference?.account_owner_last_name,
    decision: "", // TODO back
    email_address: paymentPreference?.remittance_recipient_email_addresses,
    sftp_delivery_targets: paymentPreference?.remittance_recipient_sftp_delivery_targets,
    remittance_type: ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER,
    remit: {
      check: checkSameRemittanceAddress(provider, paymentPreference),
      street_address: paymentPreference?.remittance_address_line_1,
      address_line: paymentPreference?.remittance_address_line_2,
      city: paymentPreference?.remittance_address_city,
      state: paymentPreference?.remittance_address_state,
      zip_code: paymentPreference?.remittance_address_zip_code,
    },
    provider_network_id: Number(providerNetworkId),
    transaction_validation_claim_1_number: paymentPreference?.transaction_validation_claim_1_number,
    transaction_validation_claim_1_paid_amount: paymentPreference?.transaction_validation_claim_1_paid_amount,
    transaction_validation_claim_2_number: paymentPreference?.transaction_validation_claim_2_number,
    transaction_validation_claim_2_paid_amount: paymentPreference?.transaction_validation_claim_2_paid_amount,
    authorization_letter: {
      files: [],
      notes: "",
    },
    bank_letterhead_voided_check: {
      type: documentsByType.letterhead?.document_type,
      files: [],
      notes: "",
    },
  };

  const form = useForm<ProviderPaymentRemittanceDataForm>({
    defaultValues,
  });

  const submitHandler = async (data: ProviderPaymentRemittanceDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update);

    try {
      const authLetterResult = processDocumentFiles(
        data.authorization_letter.files,
        data.authorization_letter.notes,
        "authorization_letter_for_eft",
      );

      const bankLetterResult = processDocumentFiles(
        data.bank_letterhead_voided_check.files,
        data.bank_letterhead_voided_check.notes,
        data.bank_letterhead_voided_check.type,
      );

      const fileObjects = [...authLetterResult.fileObjects, ...bankLetterResult.fileObjects];
      const files = [...authLetterResult.fileMetadata, ...bankLetterResult.fileMetadata];

      const hasNewFiles = fileObjects.length > 0;

      if (hasNewFiles) {
        const response = await getProviderManagementDocumentsUrlAPI({ network_id: data.provider_network_id, files }, getAuthToken());

        if (!response.successful_files || response.successful_files.length === 0) {
          const errorMessage = response.errors?.map((err) => err.message).join("; ") || "No files were successfully processed";
          showAlert({
            type: "error",
            style: "banner",
            message: errorMessage,
          });
          return;
        }

        const fileMap = new Map<string, File>();
        fileObjects.forEach((obj) => fileMap.set(obj.fileName, obj.file));

        let successfulUploads = 0;
        const totalFilesToUpload = response.successful_files.length;

        for (const fileResult of response.successful_files) {
          try {
            const file = fileMap.get(fileResult.file_name);
            if (file && fileResult.post) {
              await uploadFileToS3(file, fileResult.post);
              successfulUploads++;
            }
          } catch (error) {
            console.error(`Error uploading file ${fileResult.file_name}:`, error);
          }
        }

        setDocumentList(documentList.concat(response.successful_files as ProviderManagement.DocumentSchema[]));

        if (successfulUploads === totalFilesToUpload) {
          showAlert({
            type: "success",
            style: "banner",
            message: "Successfully uploaded file",
          });
        } else if (successfulUploads > 0) {
          showAlert({
            type: "success",
            style: "banner",
            message: `${successfulUploads} of ${totalFilesToUpload} documents uploaded successfully`,
          });
        } else {
          showAlert({
            type: "error",
            style: "banner",
            message: "Failed to upload documents",
          });
          return;
        }
      }

      // --- Payment Preference Logic ---
      const remittanceAddress = {} as DeepMutable<
        Pick<
          ProviderManagement.PaymentPreferenceSchema,
          | "remittance_address_line_1"
          | "remittance_address_line_2"
          | "remittance_address_city"
          | "remittance_address_state"
          | "remittance_address_zip_code"
        >
      >;

      if (data.remittance_type === ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER) {
        remittanceAddress.remittance_address_line_1 = data.remit.check ? provider.mailing_address_line_1 : data.remit.street_address;
        remittanceAddress.remittance_address_line_2 = data.remit.check ? provider.mailing_address_line_2 : data.remit.address_line;
        remittanceAddress.remittance_address_city = data.remit.check ? provider.mailing_address_city : data.remit.city;
        remittanceAddress.remittance_address_state = data.remit.check ? provider.mailing_address_state : data.remit.state;
        remittanceAddress.remittance_address_zip_code = data.remit.check ? provider.mailing_address_zip_code : data.remit.zip_code;
      }

      const payload = {
        provider_network_id: Number(providerNetworkId),
        network_enrollment_status: "in_review",
        payment_type: data.payment_type,
        financial_institution_name: data.financial_institutions.name,
        financial_institution_address_line_1: data.financial_institutions.street_address,
        financial_institution_address_line_2: data.financial_institutions.address_line,
        financial_institution_address_city: data.financial_institutions.city,
        financial_institution_address_state: data.financial_institutions.state,
        financial_institution_address_zip_code: data.financial_institutions.zip_code,
        financial_institution_contact_first_name: data.financial_institutions.first_name,
        financial_institution_contact_last_name: data.financial_institutions.last_name,
        financial_institution_phone: data.financial_institutions.phone_number,
        financial_institution_fax: data.financial_institutions.fax_number,
        ach_account_type: data.type_account,
        ach_routing_number: data.routing_number,
        ach_bank_account_number: data.bank_account_number,
        account_owner_first_name: data.account_owner_first_name,
        account_owner_last_name: data.account_owner_last_name,
        // decision: '', // TODO back
        remittance_recipient_email_addresses: data.email_address,
        remittance_recipient_sftp_delivery_targets: data.sftp_delivery_targets,
        remittance_type: data.remittance_type,
        transaction_validation_claim_1_number: data.transaction_validation_claim_1_number,
        transaction_validation_claim_1_paid_amount: data.transaction_validation_claim_1_paid_amount,
        transaction_validation_claim_2_number: data.transaction_validation_claim_2_number,
        transaction_validation_claim_2_paid_amount: data.transaction_validation_claim_2_paid_amount,
        ...remittanceAddress,
      };

      // Don't save redacted bank account numbers
      for (const field of ["ach_routing_number", "ach_bank_account_number"]) {
        if (payload[field] && payload[field].includes("*")) {
          delete payload[field];
        }
      }

      let updateResponse;
      if (paymentPreference?.id) {
        updateResponse = await updatePaymentPreferenceAPI({
          ...payload,
          id: Number(paymentPreference.id),
          network_enrollment_status: "in_review",
          provider_network_id: data.provider_network_id,
        }, getAuthToken());
      } else {
        const newPreference = await createPaymentPreferenceAPI({
          provider_network_id: data.provider_network_id,
          network_enrollment_status: "in_review",
        }, getAuthToken());
        updateResponse = await updatePaymentPreferenceAPI({
          ...payload,
          id: Number(newPreference.id),
          provider_network_id: data.provider_network_id,
        }, getAuthToken());
      }

      if (updateResponse?.success) {
        setPaymentPreference({ ...updateResponse, ...payload });
      }
      setPaymentPreference({ ...paymentPreference, ...payload });
      showAlert({
        type: "success",
        style: "banner",
        message: "Successfully submitted network configuration",
      });
      setIsEditPayment(false);
    } catch (error) {
      console.error(error);
      showAlert({
        type: "error",
        style: "banner",
        message: "Failed to save provider details. Please try again later.",
      });
      // analytics log validate fail
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }

    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update_success);
  };

  const cancelHandler = () => {
    form.reset(defaultValues);
    setIsEditPayment(false);
  };

  return (
    <Form form={form} onSubmit={submitHandler}>
      <Stack spacing="large">
        <PaymentRemittanceGroupFields
          readOnly={false}
          provider={provider}
          paymentPreference={paymentPreference}
          setPaymentPreference={setPaymentPreference}
          documents={documents}
          typeForm="edit"
        />
        <Shelf spacing="small">
          <SubmitButton appearance={getThemeButton("primary", primary_color)}>Save and Submit</SubmitButton>
          <Button appearance={getThemeButton("secondary", primary_color)} onClick={cancelHandler}>
            Cancel
          </Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};
